import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../utils/seo';

export default function Resources() {
  useSEO({
    title: 'Real Estate Resources & Tools | Home Buying & Selling Guides | Jeffery Long California',
    description: 'Free real estate resources for buyers and sellers in Northern California. Mortgage calculator, home buying guide, neighborhood information, market reports, and expert tips from Jeffery Long. Serving Sacramento, El Dorado Hills, Cameron Park. CA DRE #02234432',
    keywords: 'real estate resources, home buying guide, mortgage calculator, neighborhood guide, market reports, California real estate tools, Sacramento resources, property search, home selling tips',
    canonical: '/resources',
    ogTitle: 'Real Estate Resources & Tools | Northern California',
    ogDescription: 'Everything you need to make informed real estate decisions. Free tools, guides, and market insights.',
    ogImage: 'https://public.readdy.ai/ai/img_res/dce6376147fba96265fd72aa7a834c25.jpg',
    schema: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Real Estate Resources",
      "description": "Comprehensive real estate resources including tools, guides, and market insights for buyers and sellers in Northern California.",
      "provider": {
        "@type": "RealEstateAgent",
        "name": "Jeffery Long - eXp Realty"
      }
    }
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Real Estate Resources</h1>
            <p className="text-xl text-blue-100">
              Everything you need to make informed real estate decisions. From market insights to helpful tools and guides.
            </p>
          </div>
        </div>
      </section>

      {/* Buyer Resources */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-12">For Buyers</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-home-search-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Home Buying Guide</h3>
              <p className="text-gray-600 mb-4">
                Step-by-step guide to help you navigate the home buying process from start to finish.
              </p>
              <a href="https://jefferylong.exprealty.com/index.php?showagent=1&rtype=map" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 font-semibold whitespace-nowrap">
                Start Searching <i className="ri-arrow-right-line ml-1"></i>
              </a>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-calculator-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Mortgage Calculator</h3>
              <p className="text-gray-600 mb-4">
                Calculate your monthly mortgage payments and see how much home you can afford.
              </p>
              <a href="/finance" className="text-blue-600 hover:text-blue-700 font-semibold whitespace-nowrap">
                Calculate Now <i className="ri-arrow-right-line ml-1"></i>
              </a>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-map-pin-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Neighborhood Guide</h3>
              <p className="text-gray-600 mb-4">
                Explore different neighborhoods, schools, amenities, and local market trends.
              </p>
              <a href="/" className="text-blue-600 hover:text-blue-700 font-semibold whitespace-nowrap">
                Explore Areas <i className="ri-arrow-right-line ml-1"></i>
              </a>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-file-list-3-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">First-Time Buyer Tips</h3>
              <p className="text-gray-600 mb-4">
                Essential tips and advice for first-time home buyers to make the process easier.
              </p>
              <a href="/blog" className="text-blue-600 hover:text-blue-700 font-semibold whitespace-nowrap">
                Read More <i className="ri-arrow-right-line ml-1"></i>
              </a>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-building-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">New Construction</h3>
              <p className="text-gray-600 mb-4">
                Browse new construction homes and learn about building your dream home.
              </p>
              <a href="https://jefferylong.buildersupdate.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 font-semibold whitespace-nowrap">
                View Homes <i className="ri-arrow-right-line ml-1"></i>
              </a>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Home Inspection Checklist</h3>
              <p className="text-gray-600 mb-4">
                What to look for during a home inspection and questions to ask your inspector.
              </p>
              <a href="/blog" className="text-blue-600 hover:text-blue-700 font-semibold whitespace-nowrap">
                Get Checklist <i className="ri-arrow-right-line ml-1"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Seller Resources */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-12">For Sellers</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-price-tag-3-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Home Valuation</h3>
              <p className="text-gray-600 mb-4">
                Get a free home valuation to understand your property's current market value.
              </p>
              <a href="/sell" className="text-green-600 hover:text-green-700 font-semibold whitespace-nowrap">
                Get Valuation <i className="ri-arrow-right-line ml-1"></i>
              </a>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-home-gear-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Preparing Your Home</h3>
              <p className="text-gray-600 mb-4">
                Tips on staging, repairs, and improvements to maximize your home's sale price.
              </p>
              <a href="/blog" className="text-green-600 hover:text-green-700 font-semibold whitespace-nowrap">
                Learn More <i className="ri-arrow-right-line ml-1"></i>
              </a>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-camera-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Professional Photography</h3>
              <p className="text-gray-600 mb-4">
                High-quality photos and virtual tours to showcase your property's best features.
              </p>
              <a href="/sell" className="text-green-600 hover:text-green-700 font-semibold whitespace-nowrap">
                View Services <i className="ri-arrow-right-line ml-1"></i>
              </a>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-line-chart-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Market Analysis</h3>
              <p className="text-gray-600 mb-4">
                Comprehensive market analysis to help you price your home competitively.
              </p>
              <a href="/sell" className="text-green-600 hover:text-green-700 font-semibold whitespace-nowrap">
                Request Analysis <i className="ri-arrow-right-line ml-1"></i>
              </a>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-article-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Selling Timeline</h3>
              <p className="text-gray-600 mb-4">
                Understand the selling process and timeline from listing to closing.
              </p>
              <a href="/blog" className="text-green-600 hover:text-green-700 font-semibold whitespace-nowrap">
                View Timeline <i className="ri-arrow-right-line ml-1"></i>
              </a>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-file-text-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Required Documents</h3>
              <p className="text-gray-600 mb-4">
                Checklist of documents and disclosures needed to sell your property.
              </p>
              <a href="/blog" className="text-green-600 hover:text-green-700 font-semibold whitespace-nowrap">
                Get Checklist <i className="ri-arrow-right-line ml-1"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Market Reports */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Market Reports &amp; Insights</h2>
            <p className="text-lg text-gray-600">
              Stay informed with the latest real estate market trends, statistics, and expert analysis.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="flex items-start mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-bar-chart-box-line text-2xl text-purple-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Monthly Market Report</h3>
                  <p className="text-gray-600">
                    Detailed analysis of local market conditions, pricing trends, and inventory levels.
                  </p>
                </div>
              </div>
              <a href="/blog" className="text-purple-600 hover:text-purple-700 font-semibold whitespace-nowrap">
                View Latest Report <i className="ri-arrow-right-line ml-1"></i>
              </a>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="flex items-start mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-newspaper-line text-2xl text-purple-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Industry News</h3>
                  <p className="text-gray-600">
                    Latest news and updates affecting the real estate market and homeowners.
                  </p>
                </div>
              </div>
              <a href="/blog" className="text-purple-600 hover:text-purple-700 font-semibold whitespace-nowrap">
                Read News <i className="ri-arrow-right-line ml-1"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 className="text-3xl font-bold mb-4">Need Personalized Guidance?</h2>
            <p className="text-xl text-blue-100 mb-8">
              Our experienced team is here to help you navigate your real estate journey with expert advice and support.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/contact" 
                className="px-8 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                Contact Us
              </a>
              <a 
                href="/agents" 
                className="px-8 py-3 bg-blue-700 text-white rounded-lg font-semibold hover:bg-blue-800 transition-colors border-2 border-white whitespace-nowrap"
              >
                Meet Our Team
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
